import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import usePageMeta from 'hooks/use-page-meta';
import { useCvssProductCatalogPayload } from 'pages/Search/components/Cvss/hooks/use-cvss-product-catalog-payload';
import { useCvssProductCatalogState } from 'pages/Search/components/Cvss/hooks/use-cvss-product-catalog-state';
import CvssFilter from 'pages/Search/components/CvssFilter';
import DataTable from 'pages/Search/components/DataTable';
import Layout from 'pages/Search/components/Layout';
import RedirectLink from 'pages/Search/components/RedirectLink';
import SectionHeading from 'pages/Search/components/SectionHeading';
import { ROUTES } from 'pages/Search/constants';
import { normalizeName } from 'pages/Search/utils';
import { useProductsCatalog } from 'queries/catalog/use-products-catalog';

import useStyles from './styles';

const Cvss: React.FC = () => {
    const { classes } = useStyles();

    const layoutRef = useRef<{ scrollTop: VoidFunction } | null>(null);
    const [state, actions] = useCvssProductCatalogState();
    const payload = useCvssProductCatalogPayload(state);
    const { data, isLoading } = useProductsCatalog(payload);

    useEffect(() => {
        if (data && layoutRef.current) {
            layoutRef.current.scrollTop();
        }
    }, [data]);

    const hasMetaPagination = state.page > 1;
    const pageMeta = hasMetaPagination ? ' - Page ' + state.page : '';

    usePageMeta({
        title: `Vulnerabilities by CVSS Score ${state.cvss ? ` - ${state.cvss}` : ''}${pageMeta} | Vulners.com`,
        description: `Vulnerabilities with CVSS Score in the range of - ${state.cvss || '0-10'}${pageMeta}`
    });

    const getPaginationHref = useCallback((page: number) => actions.url({ page }), [actions]);
    const getOptionHref = useCallback((cvss: string) => actions.url({ cvss }), [actions]);

    const tableHeaders = useMemo(
        () => [
            { field: 'vendor' as const, label: 'Vendor', sort: true },
            { field: 'product' as const, label: 'Product', sort: true },
            { field: 'cves' as const, label: 'Vulnerabilities', sort: true, width: 120 }
        ],
        []
    );

    const tableData = useMemo(
        () =>
            data?.items.map(({ vendor, product, cves }, index) => [
                <RedirectLink
                    key={`vendor-${index}`}
                    to={ROUTES.VENDOR({ page: null, vendor, score: payload.score })}
                    label={normalizeName(vendor)}
                    active={!!vendor}
                    className={classes.link}
                />,
                <RedirectLink
                    key={`product-${index}`}
                    to={ROUTES.PRODUCT_VERSIONS({
                        page: null,
                        vendor: vendor,
                        product: product,
                        score: payload.score
                    })}
                    label={normalizeName(product)}
                    active={!!vendor && !!product}
                    className={classes.link}
                />,
                <RedirectLink
                    key={`cves-${index}`}
                    to={ROUTES.PRODUCT_CVES({
                        page: null,
                        vendor: vendor,
                        product: product,
                        score: payload.score as never
                    })}
                    label={cves}
                    active={!!vendor && !!product && cves > 0}
                    className={classes.link}
                />
            ]) ?? [],
        [classes.link, data?.items, payload.score]
    );

    const handlePageChange = useCallback((page: number) => actions.set({ page }), [actions]);
    const handlePageSizeChange = useCallback((size: number) => actions.set({ size }), [actions]);
    const handleScoreSelect = useCallback((cvss: string) => actions.set({ cvss }), [actions]);

    return (
        <Layout
            ref={layoutRef}
            scrollable
            main={
                <div className={classes.content}>
                    <SectionHeading
                        title={['Vulnerabilities', state.cvss]}
                        total={data?.total ?? NaN}
                        breadcrumbs={[
                            { to: ROUTES.CVSS(), label: 'Vulnerabilities' },
                            state.cvss ? { to: ROUTES.CVSS(), label: state.cvss } : null
                        ]}
                    />
                    <CvssFilter selected={state.cvss} getOptionHref={getOptionHref} onSelect={handleScoreSelect} />
                    <DataTable
                        headers={tableHeaders}
                        data={tableData}
                        loading={isLoading}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        getPaginationHref={getPaginationHref}
                        onSortingChange={(sort) => actions.set({ sort })}
                        page={state.page}
                        pageSize={state.size}
                        totalCount={data?.total}
                        sorting={state.sort}
                    />
                </div>
            }
        />
    );
};

export default Cvss;
